/**
 * Size Guide Manager
 * Handles drawer initialization, tabs, and unit conversion
 */
class SizeGuideManager {
  constructor() {
    this.isMetric = true;
    this.drawerInitialized = false;
    this.storageKey = 'unitPreference';
    this.drawer = null;
    this.isDrawerOpen = false;
    
    this.init();
  }

  init() {
    this.loadPreference();
    this.setupEventDelegation();
    this.initializeDrawer();
    this.setupMutationObserver();
    
    // Wait for DOM to be ready before processing tables
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.processDrawerContent();
      });
    } else {
      this.processDrawerContent();
    }
  }

  /**
   * Setup event delegation (only once)
   */
  setupEventDelegation() {
    // Unit toggle change handler
    document.addEventListener('change', (event) => {
      if (event.target.matches('.unit-toggle input[type="radio"]')) {
        this.toggleUnits(event.target.value === 'metric');
      }
    });

    // Tab click handler
    document.addEventListener('click', (event) => {
      const tabButton = event.target.closest('button[data-tab]');
      if (tabButton) {
        this.handleTabClick(event);
      }
    });
  }

  /**
   * Setup mutation observer to detect when drawer content changes (variant change)
   */
  setupMutationObserver() {
    // Observe document for drawer changes and radio button additions
    const observer = new MutationObserver((mutations) => {
      let shouldReprocess = false;
      let hasNewRadios = false;

      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            // Check if drawer was re-added
            if (node.classList?.contains('js-size-drawer') || node.querySelector?.('.js-size-drawer')) {
              shouldReprocess = true;
            }
            
            // Check if radio buttons were added
            if (node.classList?.contains('unit-toggle') || node.querySelector?.('.unit-toggle')) {
              hasNewRadios = true;
            }
          }
        });
      });

      // If new radio buttons detected, immediately update their state
      if (hasNewRadios) {
        // Use multiple attempts to ensure it catches
        this.updateToggleInputs();
        setTimeout(() => this.updateToggleInputs(), 0);
        setTimeout(() => this.updateToggleInputs(), 10);
        setTimeout(() => this.updateToggleInputs(), 50);
      }

      // If drawer was re-rendered
      if (shouldReprocess) {
        this.drawer = document.querySelector('.js-size-drawer');
        if (this.isDrawerOpen) {
          // Re-process content and keep drawer open
          setTimeout(() => {
            this.processDrawerContent();
            this.ensureDrawerOpen();
          }, 10);
        }
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  /**
   * Ensure drawer stays open after content update
   */
  ensureDrawerOpen() {
    if (this.drawer && this.isDrawerOpen) {
      // Make sure drawer is in body
      if (!this.drawer.parentElement.matches('body')) {
        document.body.appendChild(this.drawer);
      }
      
      // Re-open drawer if it was closed
      if (this.drawer.getAttribute('aria-hidden') === 'true') {
        this.drawer.setAttribute('aria-hidden', 'false');
        this.drawer.style.display = '';
      }
    }
  }

  /**
   * Process drawer content (store values, attach listeners, apply preferences)
   */
  processDrawerContent() {
    this.storeOriginalValues();
    this.initializeTabs();
    this.applyUnitPreference();
  }

  /**
   * Initialize drawer and move to body if needed
   */
  initializeDrawer() {
    // Use event delegation for drawer open button
    document.addEventListener('click', (event) => {
      const button = event.target.closest('.js-show-size-info');
      if (!button) return;

      this.drawer = document.querySelector('.js-size-drawer');
      if (!this.drawer) return;

      // Move drawer to body on first open for better layering
      if (!this.drawer.parentElement.matches('body')) {
        document.body.appendChild(this.drawer);
        this.drawerInitialized = true;
      }

      // Initialize tabs when drawer opens
      this.initializeTabs();
      this.processDrawerContent();

      // Mark drawer as open
      this.isDrawerOpen = true;

      // Open drawer
      if (typeof this.drawer.open === 'function') {
        this.drawer.open(button);
      }
    });

    // Listen for drawer close events
    document.addEventListener('click', (event) => {
      const closeButton = event.target.closest('.js-close-drawer');
      if (closeButton) {
        this.isDrawerOpen = false;
      }
    });

    // Listen for backdrop clicks to close drawer
    document.addEventListener('click', (event) => {
      if (event.target.classList?.contains('drawer-backdrop')) {
        this.isDrawerOpen = false;
      }
    });

    // Listen for escape key
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isDrawerOpen) {
        this.isDrawerOpen = false;
      }
    });
  }

  /**
   * Initialize tab functionality
   */
  initializeTabs() {
    const tabsContainers = document.querySelectorAll('.drawer__tabs-container');
    
    tabsContainers.forEach((tabsContainer) => {
      if (tabsContainer.dataset.tabsInitialised) return;

      const tabsList = tabsContainer.querySelector('nav');
      const tabButtons = tabsList?.querySelectorAll('button');
      const tabPanels = tabsContainer.querySelectorAll('.tabs__panels > div');

      if (!tabButtons?.length || !tabPanels?.length) return;

      // Set initial ARIA attributes
      tabButtons.forEach((tab, index) => {
        tab.setAttribute('role', 'tab');

        if (index === 0) {
          tab.setAttribute('aria-selected', 'true');
          tab.removeAttribute('tabindex');
        } else {
          tab.setAttribute('aria-selected', 'false');
          tab.setAttribute('tabindex', '-1');
          tabPanels[index]?.setAttribute('hidden', '');
        }
      });

      tabPanels.forEach((panel) => {
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('tabindex', '0');
      });

      tabsContainer.dataset.tabsInitialised = 'true';
    });
  }

  /**
   * Handle tab click events
   */
  handleTabClick(event) {
    const tabButton = event.target.closest('button[data-tab]');
    if (!tabButton) return;

    const tabsContainer = tabButton.closest('.drawer__tabs-container');
    if (!tabsContainer) return;

    const tabButtons = tabsContainer.querySelectorAll('button[data-tab]');
    const tabPanels = tabsContainer.querySelectorAll('.tabs__panels > div');
    const activePanel = tabsContainer.querySelector(tabButton.dataset.tab);

    // Update all tabs
    tabButtons.forEach((btn) => {
      btn.setAttribute('aria-selected', 'false');
      btn.setAttribute('tabindex', '-1');
    });

    // Hide all panels
    tabPanels.forEach((panel) => {
      panel.setAttribute('hidden', 'true');
    });

    // Show active panel
    if (activePanel) {
      activePanel.removeAttribute('hidden');
    }

    // Update active tab
    tabButton.setAttribute('aria-selected', 'true');
    tabButton.setAttribute('tabindex', '0');
    tabButton.focus();

    // Scroll drawer content to top
    const drawerContent = tabsContainer.closest('.drawer')?.querySelector('.drawer__content');
    if (drawerContent) {
      drawerContent.scrollTop = 0;
    }
  }

  /**
   * Store original metric values in data attributes
   */
  storeOriginalValues() {
    const rows = document.querySelectorAll('.measurement-table tr');
    
    rows.forEach((row) => {
      const cells = row.querySelectorAll('td');
      
      cells.forEach((cell, index) => {
        // Skip first column (usually size labels)
        if (index > 0) {
          const text = cell.textContent.trim();
          
          // Only store if not already stored
          if (!cell.hasAttribute('data-original-metric')) {
            const parsed = this.parseMetricValue(text);
            
            if (parsed.type !== 'invalid') {
              cell.setAttribute('data-metric', text);
              cell.setAttribute('data-original-metric', text);
            }
          }
        }
      });
    });
  }

  /**
   * Parse metric value (handles single numbers, ranges, and slash notation)
   */
  parseMetricValue(value) {
    // Match ranges like "85-90" or "85–90" (en dash) or "85—90" (em dash)
    const rangeRegex = /^(\d+(?:\.\d+)?)[\s–—-]+(\d+(?:\.\d+)?)$/;
    // Match slash notation like "85/90"
    const slashRegex = /^(\d+(?:\.\d+)?)(?:\s*)\/(?:\s*)(\d+(?:\.\d+)?)$/;

    const rangeMatch = value.match(rangeRegex);
    const slashMatch = value.match(slashRegex);

    if (rangeMatch) {
      return {
        type: 'range',
        values: [parseFloat(rangeMatch[1]), parseFloat(rangeMatch[2])]
      };
    } else if (slashMatch) {
      return {
        type: 'slash',
        values: [parseFloat(slashMatch[1]), parseFloat(slashMatch[2])]
      };
    } else if (!isNaN(parseFloat(value))) {
      return {
        type: 'single',
        values: [parseFloat(value)]
      };
    } else {
      return {
        type: 'invalid',
        values: [value]
      };
    }
  }

  /**
   * Round to nearest quarter for imperial measurements
   */
  roundToNearestQuarter(value) {
    return Math.round(value * 4) / 4;
  }

  /**
   * Toggle between metric and imperial units
   */
  toggleUnits(toMetric = null) {
    if (typeof toMetric === 'boolean') {
      this.isMetric = toMetric;
    } else {
      this.isMetric = !this.isMetric;
    }

    const rows = document.querySelectorAll('.measurement-table tr');
    
    rows.forEach((row) => {
      const cells = row.querySelectorAll('td');
      
      cells.forEach((cell, index) => {
        if (index > 0 && cell.hasAttribute('data-metric')) {
          const metricValue = cell.getAttribute('data-metric').trim();
          const parsed = this.parseMetricValue(metricValue);
          let convertedValue = metricValue;

          if (!this.isMetric) {
            // Convert metric to imperial (cm to inches)
            const converted = parsed.values.map((val) =>
              this.roundToNearestQuarter(val / 2.54)
            );
            
            if (parsed.type === 'range') {
              convertedValue = `${converted[0]}–${converted[1]}`;
            } else if (parsed.type === 'slash') {
              convertedValue = `${converted[0]}/${converted[1]}`;
            } else {
              convertedValue = `${converted[0]}`;
            }
          } else {
            // Show original metric value
            convertedValue = cell.getAttribute('data-original-metric');
          }

          cell.textContent = convertedValue;
        }
      });
    });

    // Save preference
    this.savePreference();

    // Update all toggle radio inputs
    this.updateToggleInputs();
  }

  /**
   * Update all unit toggle radio inputs to match current state
   * This method directly manipulates both the input state and label styles
   */
  updateToggleInputs() {
    document.querySelectorAll('.unit-toggle').forEach((group) => {
      const imperialRadio = group.querySelector('input[value="imperial"]');
      const metricRadio = group.querySelector('input[value="metric"]');
      const imperialLabel = imperialRadio?.nextElementSibling;
      const metricLabel = metricRadio?.nextElementSibling;
      
      if (this.isMetric) {
        // Set metric as active
        if (metricRadio) {
          metricRadio.checked = true;
          metricRadio.setAttribute('checked', 'checked');
        }
        if (metricLabel) {
          metricLabel.style.background = '#000';
          metricLabel.style.color = '#fff';
        }
        
        // Set imperial as inactive
        if (imperialRadio) {
          imperialRadio.checked = false;
          imperialRadio.removeAttribute('checked');
        }
        if (imperialLabel) {
          imperialLabel.style.background = '';
          imperialLabel.style.color = '';
        }
      } else {
        // Set imperial as active
        if (imperialRadio) {
          imperialRadio.checked = true;
          imperialRadio.setAttribute('checked', 'checked');
        }
        if (imperialLabel) {
          imperialLabel.style.background = '#000';
          imperialLabel.style.color = '#fff';
        }
        
        // Set metric as inactive
        if (metricRadio) {
          metricRadio.checked = false;
          metricRadio.removeAttribute('checked');
        }
        if (metricLabel) {
          metricLabel.style.background = '';
          metricLabel.style.color = '';
        }
      }
    });
  }

  /**
   * Apply unit preference on page load
   */
  applyUnitPreference() {
    this.toggleUnits(this.isMetric);
  }

  /**
   * Save preference to localStorage
   */
  savePreference() {
    try {
      localStorage.setItem(this.storageKey, this.isMetric ? 'metric' : 'imperial');
    } catch (e) {
      console.warn('Could not save unit preference:', e);
    }
  }

  /**
   * Load preference from localStorage
   */
  loadPreference() {
    try {
      const preference = localStorage.getItem(this.storageKey);
      this.isMetric = preference !== 'imperial';
    } catch (e) {
      console.warn('Could not load unit preference:', e);
      this.isMetric = true; // Default to metric
    }
  }
}

// Initialize the size guide manager
new SizeGuideManager();